/*
 * MedTrack room tiles: what one board row (medtrack/get | medtrack/fetch) puts on a tile (TSK-8074).
 *
 * Call for help: the room tablet's non-emergency flag (api room_dashboard/helpflag.py) stored as
 *   appointment_master.signintime.help_request = {status, id, room, requestedAt, closedAt?}.
 * The API sends it per row as `help_request` = {open, opened_at, minutes, room, id, closed_at} (or
 * null). Until that API is live the raw signintime.help_request is read instead. Staff close it from
 * care's Waiting Room ("Done" -> POST /v1/{p}/room-dashboard/help-requests/close); the next board poll
 * then gets open=false and the tile badge disappears. The board itself never closes anything.
 *
 * Wait-time bell: roomed for more than 20 minutes and no BlinkScribe recording started. The API
 * used to send only the misspelled `StartAudiorecording` (always 0) by default, and the board never
 * copied the field into the tile, so the bell could never ring. Both spellings are read now.
 */

export const BELL_AFTER_MINUTES = 20;

const asObject = (v) => {
  if (v && typeof v === "object") return v;
  if (typeof v === "string" && v.trim().startsWith("{")) {
    try {
      const o = JSON.parse(v);
      return o && typeof o === "object" ? o : null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// "YYYY-MM-DD HH:MM:SS" (server local time, as helpflag writes it) -> whole minutes before `now`.
const minutesSince = (stamp, now) => {
  if (!stamp) return 0;
  const m = String(stamp).trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return 0;
  const t = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)).getTime();
  const n = (now instanceof Date ? now : new Date()).getTime();
  return Math.max(0, Math.floor((n - t) / 60000));
};

// {open, minutes, openedAt, room} of the row's help request, or null when none is open.
export const boardHelpRequest = (apt, now) => {
  if (!apt) return null;
  const api = asObject(apt.help_request);
  if (api) {
    if (api.open !== true) return null;
    const minutes = Number.isFinite(Number(api.minutes)) ? Math.max(0, Math.floor(Number(api.minutes)))
      : minutesSince(api.opened_at, now);
    return { open: true, minutes, openedAt: api.opened_at || null, room: api.room || "" };
  }
  if (apt.help_request === null) return null;                 // new API: explicitly none
  const raw = asObject(asObject(apt.signintime)?.help_request); // older API: raw signintime only
  if (!raw || String(raw.status || "").toLowerCase() !== "open") return null;
  return { open: true, minutes: minutesSince(raw.requestedAt, now), openedAt: raw.requestedAt || null,
           room: raw.room || "" };
};

// The longest-open request of a tile's occupants (OSA's shared rooms), or null.
export const tileHelpRequest = (rows, now) =>
  (rows || []).map((r) => boardHelpRequest(r, now)).filter(Boolean)
    .sort((a, b) => b.minutes - a.minutes)[0] || null;

// Minutes since the recording started; 0 = none started. Either spelling the API sends.
export const startAudioRecordingOf = (apt) => {
  const v = apt?.startAudioRecording ?? apt?.StartAudiorecording ?? 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// The tile's appointmentData for one row (`occupants` only on OSA's board). `helpRows`: every row in
// the room (care's board shows only the first patient of a room, but a help request from any
// patient in it lights the tile).
export const roomAppointmentData = (appointment, occupants, { helpRows, now } = {}) => ({
  start_time: appointment.start_time,
  id: appointment.id,
  patientid: appointment.patientid,
  firstname: appointment.firstname,
  lastname: appointment.lastname,
  token: appointment.token,
  linked_provider: appointment.linked_provider,
  room_no: appointment.room_no,
  roomAssignInterval: appointment.roomAssignInterval || 0,
  recordingBy: appointment.recordingBy,
  startRecording: appointment.startRecording,
  stopRecording: appointment.stopRecording,
  startAudioRecording: startAudioRecordingOf(appointment),
  helpRequest: tileHelpRequest(helpRows && helpRows.length ? helpRows
    : (occupants && occupants.length ? occupants : [appointment]), now),
  ...(occupants ? { occupants } : {}),
});

// Wait-time bell: roomed > 20 min and no recording started.
export const isWaitBell = (appointmentData) =>
  Boolean(appointmentData) &&
  Number(appointmentData.roomAssignInterval) > BELL_AFTER_MINUTES &&
  appointmentData.startRecording !== "Y" &&
  startAudioRecordingOf(appointmentData) === 0;

// "just now" / "4 min" / "1 h 05 min"
export const helpAgeLabel = (minutes) => {
  const m = Math.max(0, Math.floor(Number(minutes) || 0));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
};

// Open help requests whose patient is on no room tile (no room_no, or a room this board does not
// show): listed in the header so a request is never invisible on the board.
export const unmatchedHelpRequests = (rows, roomNames, now) => {
  const shown = new Set((roomNames || []).map((n) => String(n).trim()));
  const out = [];
  (rows || []).forEach((r) => {
    const hr = boardHelpRequest(r, now);
    if (!hr) return;
    const room = r.room_no == null ? "" : String(r.room_no).trim();
    if (room && shown.has(room)) return;
    out.push({ key: r.appointmentid || r.id || r.patientid, room: room || hr.room || "", minutes: hr.minutes });
  });
  return out;
};
